import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/community.css';
import { useLanguage } from '../../utils/LanguageContext';
import { useAuth } from '../../utils/useAuth';
import { getLocalized } from '../../utils/languageUtils';
import { CountUp } from '../../utils/motion';
import api from '../../services/api';

const STICKER = (name) => `/images/stickers/${name}.webp`;

// Show only a first name and last initial — never emails, phones or birth dates.
function displayName(profile) {
  const first = profile?.user?.first_name?.trim();
  const last = profile?.user?.last_name?.trim();
  if (first) return last ? `${first} ${last[0]}.` : first;
  return profile?.name || '—';
}

const hasLocation = (p) => {
  const lat = parseFloat(p.latitude);
  const lng = parseFloat(p.longitude ?? p.lognitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

const Community = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState({ trees: 0, members: 0 });
  const [planted, setPlanted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/general/')
      .then(res => setStats({
        trees: res.data?.countPLantedTrees || 0,
        members: res.data?.countUsers || 0,
      }))
      .catch(() => {});

    api.get('map/')
      .then(res => setPlanted(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error('Community data error:', err))
      .finally(() => setLoading(false));
  }, []);

  // Leaderboard: trees planted per member, from real planting records.
  const leaderboard = useMemo(() => {
    const byOwner = new Map();
    planted.forEach(p => {
      const owner = p.bucket?.user;
      if (!owner?.id) return;
      const row = byOwner.get(owner.id) || {
        id: owner.id,
        name: displayName(owner),
        region: owner.region || '—',
        trees: 0,
        last: null,
      };
      row.trees += 1;
      if (p.plantingDate && (!row.last || p.plantingDate > row.last)) row.last = p.plantingDate;
      byOwner.set(owner.id, row);
    });
    return [...byOwner.values()]
      .sort((a, b) => b.trees - a.trees || (b.last || '').localeCompare(a.last || ''))
      .slice(0, 10);
  }, [planted]);

  const regionCount = useMemo(
    () => new Set(planted.map(p => p.bucket?.user?.region).filter(Boolean)).size,
    [planted]
  );

  // Latest plantings, one card per member + species so a bulk order of many
  // identical trees doesn't fill the whole feed.
  const recent = useMemo(() => {
    const seen = new Set();
    return [...planted]
      .filter(p => p.plantingDate)
      .sort((a, b) => b.plantingDate.localeCompare(a.plantingDate))
      .filter(p => {
        const key = `${p.bucket?.user?.id}-${p.bucket?.product?.tree?.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 6);
  }, [planted]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(language === 'en' ? 'en-GB' : language === 'ru' ? 'ru-RU' : 'uz-UZ', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const primaryAction = () => navigate(isAuthenticated ? '/shop' : '/auth?mode=register');
  const primaryLabel = isAuthenticated ? t('comm_btn_plant') : t('comm_btn_join');

  return (
    <div className="community-page">
      {/* Hero with live numbers */}
      <section className="comm-hero">
        <div className="comm-hero-bg" aria-hidden="true" />
        <div className="comm-hero-inner">
          <h1 className="hero-enter" style={{ '--d': '0.1s' }}>
            {t('comm_hero_title')} <span className="highlight">{t('comm_hero_highlight')}</span>
          </h1>
          <p className="hero-enter" style={{ '--d': '0.2s' }}>{t('comm_hero_desc')}</p>
          <div className="hero-actions hero-enter" style={{ '--d': '0.3s' }}>
            <button className="btn-hero-primary" onClick={primaryAction}>
              {primaryLabel} <i className="fas fa-arrow-right" />
            </button>
            <button
              className="btn-hero-secondary"
              onClick={() => document.getElementById('leaderboard')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <i className="fas fa-trophy" /> {t('comm_btn_leaderboard')}
            </button>
          </div>
          <div className="comm-stats hero-enter" style={{ '--d': '0.4s' }}>
            <div className="comm-stat">
              <img src={STICKER('mascot')} alt="" />
              <div><CountUp value={stats.members} className="comm-stat-num" /><span>{t('comm_stat_members')}</span></div>
            </div>
            <div className="comm-stat">
              <img src={STICKER('sapling-pin')} alt="" />
              <div><CountUp value={stats.trees} className="comm-stat-num" /><span>{t('comm_stat_planted')}</span></div>
            </div>
            <div className="comm-stat">
              <img src={STICKER('map')} alt="" />
              <div><CountUp value={regionCount} className="comm-stat-num" /><span>{t('comm_stat_regions')}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="section leaderboard-section" id="leaderboard">
        <div className="section-header">
          <h2 className="section-title">{t('comm_leaderboard_title')}</h2>
          <p className="section-subtitle">{t('comm_subtitle_leaderboard')}</p>
        </div>

        {loading ? (
          <div className="comm-loading">{t('comm_loading')}</div>
        ) : leaderboard.length === 0 ? (
          <div className="comm-empty">
            <img src={STICKER('basket-sprout')} alt="" />
            <p>{t('comm_empty')}</p>
          </div>
        ) : (
          <div className="ranking-container">
            <div className="ranking-wrapper">
              <table className="ranking-table">
                <thead>
                  <tr>
                    <th className="rank-col">{t('comm_rank')}</th>
                    <th>{t('comm_user')}</th>
                    <th>{t('comm_region')}</th>
                    <th className="num-col">{t('comm_trees_planted')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row, i) => (
                    <tr key={row.id}>
                      <td className="rank-col">
                        {i < 3
                          ? <i className={`fas ${i === 0 ? 'fa-crown' : 'fa-medal'} rank-${i + 1}`} />
                          : <span className="rank-badge">{i + 1}</span>}
                      </td>
                      <td>
                        <div className="rank-user">
                          <span className="rank-initial" aria-hidden="true">{row.name[0]}</span>
                          <span className="rank-name">{row.name}</span>
                        </div>
                      </td>
                      <td>{row.region}</td>
                      <td className="rank-score num-col">{row.trees}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Recent plantings */}
      {recent.length > 0 && (
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">{t('comm_recent_title')}</h2>
            <p className="section-subtitle">{t('comm_recent_sub')}</p>
          </div>
          <div className="recent-grid">
            {recent.map(p => {
              const tree = p.bucket?.product?.tree;
              const located = hasLocation(p);
              return (
                <div className="recent-card" key={p.id}>
                  <div className="recent-img">
                    {tree?.image && <img src={p.images?.[0]?.image || tree.image} alt="" loading="lazy" />}
                    <span className="recent-date">{formatDate(p.plantingDate)}</span>
                  </div>
                  <div className="recent-body">
                    <h3>{getLocalized(tree, language, 'name') || '—'}</h3>
                    <p>
                      {t('comm_planted_for')}: <strong>{displayName(p.bucket?.user)}</strong>
                      {p.bucket?.user?.region ? ` · ${p.bucket.user.region}` : ''}
                    </p>
                    {located ? (
                      <Link to={`/map?tree=${p.id}`} className="recent-link">
                        <i className="fas fa-location-dot" /> {t('comm_view_map')}
                      </Link>
                    ) : (
                      <span className="recent-link muted"><i className="far fa-clock" /> {t('comm_no_location')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Call to action */}
      <section className="section comm-cta-wrap">
        <div className="comm-cta" data-reveal="scale">
          <img src={STICKER('mascot')} alt="" className="comm-cta-mascot" />
          <div>
            <h2>{t('comm_cta_title')}</h2>
            <p>{t('comm_cta_desc')}</p>
          </div>
          <button className="btn-hero-primary" onClick={primaryAction}>
            {primaryLabel} <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default Community;
